import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText,
  Globe,
  RefreshCw,
  MoreHorizontal,
  Sparkles,
  Plus,
  ChevronUp,
  Mic,
  ArrowUp,
  Users,
  Database,
  X
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('apps');
  const [chatInput, setChatInput] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState('');
  const [animationPhase, setAnimationPhase] = useState('typing'); // 'typing', 'pausing', 'deleting'
  const [isTyping, setIsTyping] = useState(true);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState({ id: 'data-analysis', name: 'Data Analysis', icon: '🔍' });
  const [selectedModel, setSelectedModel] = useState({ id: 'gpt4', name: 'GPT-4o', provider: 'OpenAI' });
  
  // Data connectors for each workspace (scoped)
  const workspaceConnectors = {
    'manufacturing': [
      { id: 'snowflake-1', name: 'Snowflake', type: 'snowflake', tables: 8 },
      { id: 'databricks-1', name: 'Databricks', type: 'databricks', tables: 6 },
      { id: 'postgres-1', name: 'PostgreSQL', type: 'postgresql', tables: 5 },
    ],
    'healthcare': [
      { id: 'snowflake-2', name: 'Snowflake', type: 'snowflake', tables: 4 },
      { id: 'bigquery-1', name: 'BigQuery', type: 'bigquery', tables: 3 },
    ],
    'cpg': [],
    'smart-buildings': [
      { id: 'databricks-2', name: 'Databricks', type: 'databricks', tables: 2 },
    ],
  };

  // Get current workspace's connectors (using manufacturing as default for now)
  // In real implementation, this would come from context/state management
  const currentWorkspaceId = 'manufacturing';
  const currentConnectors = workspaceConnectors[currentWorkspaceId] || [];

  // Connector logos mapping (using publicly available SVG logos)
  const connectorLogos = {
    snowflake: 'https://cdn.simpleicons.org/snowflake/29B5E8',
    databricks: 'https://cdn.simpleicons.org/databricks/FF3621',
    postgresql: 'https://cdn.simpleicons.org/postgresql/4169E1',
    bigquery: 'https://cdn.simpleicons.org/googlebigquery/669DF6',
    mysql: 'https://cdn.simpleicons.org/mysql/4479A1',
    mongodb: 'https://cdn.simpleicons.org/mongodb/47A248',
    redshift: 'https://cdn.simpleicons.org/amazonredshift/8C4FFF',
  };

  const sampleQuestions = [
    "Build a Customer 360 data product",
    "Show me revenue trends by region",
    "Create a sales analytics dashboard",
    "What are my top performing products?",
    "Analyze customer churn patterns"
  ];

  const agents = {
    dataApps: [
      { id: 'data-products', name: 'Data Products', icon: '📦' },
      { id: 'dashboards', name: 'Dashboards', icon: '📊' },
      { id: 'chatbots', name: 'Chatbots', icon: '💬' },
      { id: 'ai-models', name: 'AI Models', icon: '🤖' },
    ],
    dataDiscovery: [
      { id: 'semantic-graph', name: 'Semantic Data Graph', icon: '🕸️' },
      { id: 'data-transformation', name: 'Data Transformation', icon: '🔄' },
      { id: 'data-analysis', name: 'Data Analysis', icon: '🔍' },
    ]
  };

  const models = [
    { id: 'gpt4', name: 'GPT-4o', provider: 'OpenAI' },
    { id: 'gpt4-turbo', name: 'GPT-4 Turbo', provider: 'OpenAI' },
    { id: 'claude', name: 'Claude 3.5', provider: 'Anthropic' },
    { id: 'gemini', name: 'Gemini Pro', provider: 'Google' },
  ];

  // Mock data
  const recentTasks = [
    { 
      id: 'EMT-42cc7d', 
      title: 'b2b-navigation',
      fork: 1,
      task: 'The user wants to build a B2B data platform named "Intugle...', 
      lastModified: 'Few seconds ago' 
    },
    { 
      id: 'EMT-e35bcc', 
      title: 'intugle-dataflow',
      fork: 1,
      task: 'The user\'s initial goal was a major refactoring of the app...', 
      lastModified: '3 hrs ago' 
    },
    { 
      id: 'EMT-05ba16', 
      title: 'convert-data',
      fork: null,
      task: 'Transform Data', 
      lastModified: '17 hrs ago' 
    },
    { 
      id: 'EMT-20e489', 
      title: 'reactviz-plotly',
      fork: 1,
      task: 'The user wants to build a nice-looking React.js library of...', 
      lastModified: '1 day ago' 
    },
    { 
      id: 'EMT-df5801', 
      title: 'runapp-7',
      fork: null,
      task: 'run the app\\n', 
      lastModified: '1 day ago' 
    },
    { 
      id: 'EMT-a74b22', 
      title: 'vizcomponent',
      fork: 1,
      task: 'Help me build a nice looking library react.js library of charts or reusable', 
      lastModified: '1 day ago' 
    },
  ];

  const deployedApps = [
    { 
      id: 'APP-001', 
      name: 'Customer 360 Analytics', 
      type: 'Data Product',
      status: 'completed', 
      rows: 12500,
      columns: 24,
      executionTime: 2.3,
      dqScore: 94,
      dataSources: ['Snowflake', 'PostgreSQL'],
      createdBy: 'John D.',
      createdDate: 'Jan 15, 2025',
      lastRefreshed: '2 hrs ago',
      sharedWith: ['Marketing Team', 'Sales Team', 'Analytics Team', 'Product Team', 'Engineering', 'Customer Success', 'Finance', 'Operations', 'HR', 'Legal', 'Executive Team', 'Data Science', 'QA Team', 'DevOps', 'Support Team', 'Design Team']
    },
    { 
      id: 'APP-002', 
      name: 'Revenue Dashboard', 
      type: 'Visualization',
      status: 'running', 
      rows: 8200,
      columns: 18,
      executionTime: 1.8,
      dqScore: 97,
      dataSources: ['Snowflake'],
      createdBy: 'Sarah M.',
      createdDate: 'Jan 12, 2025',
      lastRefreshed: '30 mins ago',
      sharedWith: ['Finance', 'Leadership']
    },
    { 
      id: 'APP-003', 
      name: 'Support Assistant', 
      type: 'Chatbot',
      status: 'completed', 
      rows: 45000,
      columns: 12,
      executionTime: 0.8,
      dqScore: 91,
      dataSources: ['PostgreSQL', 'S3'],
      createdBy: 'Mike R.',
      createdDate: 'Jan 10, 2025',
      lastRefreshed: '1 day ago',
      sharedWith: ['Support Team', 'Customer Success', 'Product Team', 'Engineering', 'QA Team']
    },
    { 
      id: 'APP-004', 
      name: 'Churn Predictor', 
      type: 'AI Model',
      status: 'failed', 
      rows: 28000,
      columns: 32,
      executionTime: 4.5,
      dqScore: 89,
      dataSources: ['Snowflake', 'PostgreSQL'],
      createdBy: 'Emily K.',
      createdDate: 'Jan 8, 2025',
      lastRefreshed: '6 hrs ago',
      sharedWith: ['Data Science', 'Product Team']
    },
    { 
      id: 'APP-005', 
      name: 'Inventory Tracker', 
      type: 'Data Product',
      status: 'draft', 
      rows: 5600,
      columns: 15,
      executionTime: 1.2,
      dqScore: 86,
      dataSources: ['S3'],
      createdBy: 'Alex T.',
      createdDate: 'Jan 5, 2025',
      lastRefreshed: '3 days ago',
      sharedWith: ['Operations']
    },
    { 
      id: 'APP-006', 
      name: 'Sales Forecast', 
      type: 'Visualization',
      status: 'live', 
      rows: 15800,
      columns: 22,
      executionTime: 3.1,
      dqScore: 92,
      dataSources: ['Snowflake'],
      createdBy: 'Lisa P.',
      createdDate: 'Jan 3, 2025',
      lastRefreshed: '4 hrs ago',
      sharedWith: ['Sales Team', 'Marketing Team', 'Finance', 'Leadership', 'Analytics Team', 'Product Team', 'Operations']
    },
  ];

  // Helper function to format numbers
  const formatNumber = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Helper function to get type border color
  const getTypeBorderColor = (type) => {
    switch (type) {
      case 'Data Product': return 'border-l-blue-500';
      case 'Visualization': return 'border-l-purple-500';
      case 'Chatbot': return 'border-l-green-500';
      case 'AI Model': return 'border-l-orange-500';
      default: return 'border-l-gray-500';
    }
  };

  // Helper function to get type text color with background
  const getTypeStyles = (type) => {
    switch (type) {
      case 'Data Product': return 'text-blue-400 bg-blue-500/10';
      case 'Visualization': return 'text-purple-400 bg-purple-500/10';
      case 'Chatbot': return 'text-green-400 bg-green-500/10';
      case 'AI Model': return 'text-orange-400 bg-orange-500/10';
      default: return 'text-gray-400 bg-gray-500/10';
    }
  };

  // Helper function to get data source icon
  const getSourceIcon = (source) => {
    switch (source) {
      case 'Snowflake': return '❄️';
      case 'PostgreSQL': return '🐘';
      case 'S3': return '📦';
      default: return '📁';
    }
  };

  // Typing animation effect
  useEffect(() => {
    // Don't animate if user is typing or input is focused
    if (!isTyping || chatInput) {
      return;
    }

    const currentQuestion = sampleQuestions[placeholderIndex];
    let timeout;

    if (animationPhase === 'typing') {
      if (displayedPlaceholder.length < currentQuestion.length) {
        // Type next character
        timeout = setTimeout(() => {
          setDisplayedPlaceholder(currentQuestion.slice(0, displayedPlaceholder.length + 1));
        }, 50);
      } else {
        // Finished typing, move to pause phase
        setAnimationPhase('pausing');
      }
    } else if (animationPhase === 'pausing') {
      // Wait before deleting
      timeout = setTimeout(() => {
        setAnimationPhase('deleting');
      }, 2000);
    } else if (animationPhase === 'deleting') {
      if (displayedPlaceholder.length > 0) {
        // Delete character
        timeout = setTimeout(() => {
          setDisplayedPlaceholder(displayedPlaceholder.slice(0, -1));
        }, 30);
      } else {
        // Move to next question and start typing
        setPlaceholderIndex((prev) => (prev + 1) % sampleQuestions.length);
        setAnimationPhase('typing');
      }
    }

    return () => clearTimeout(timeout);
  }, [placeholderIndex, isTyping, chatInput, displayedPlaceholder, animationPhase, sampleQuestions]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowAgentDropdown(false);
      setShowModelDropdown(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (chatInput.trim()) {
      navigate('/app/session', { state: { pendingQuery: chatInput } });
    }
  };

  return (
    <div className="h-full overflow-y-auto relative bg-slate-900">
      {/* Background Pattern - Matching GridBackground from LandingPage */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Grid pattern */}
        <div className="absolute inset-0" 
             style={{
               backgroundImage: `
                 linear-gradient(rgba(59, 130, 246, 0.08) 1px, transparent 1px),
                 linear-gradient(90deg, rgba(59, 130, 246, 0.08) 1px, transparent 1px)
               `,
               backgroundSize: '50px 50px',
               maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)'
             }} 
        />

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900/20" style={{ zIndex: 1 }} />
        
        {/* Node network */}
        <div className="absolute inset-0" style={{ zIndex: 0 }}>
          {/* Large glowing nodes */}
          <div className="absolute w-16 h-16 rounded-full bg-blue-500/5 border-2 border-blue-500/40 shadow-[0_0_20px_rgba(59,130,246,0.3)]" style={{ top: '5%', left: '20%', animation: 'pulse 6s ease-in-out infinite' }} />
          <div className="absolute w-20 h-20 rounded-full bg-blue-500/5 border-2 border-blue-500/40 shadow-[0_0_25px_rgba(59,130,246,0.35)]" style={{ top: '15%', right: '25%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '1s' }} />
          <div className="absolute w-[72px] h-[72px] rounded-full bg-blue-500/5 border-2 border-blue-500/40 shadow-[0_0_22px_rgba(59,130,246,0.3)]" style={{ top: '55%', left: '15%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '2s' }} />
          <div className="absolute w-16 h-16 rounded-full bg-blue-500/5 border-2 border-blue-500/40 shadow-[0_0_20px_rgba(59,130,246,0.3)]" style={{ top: '40%', right: '10%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '3s' }} />
          <div className="absolute w-[72px] h-[72px] rounded-full bg-blue-500/5 border-2 border-blue-500/40 shadow-[0_0_22px_rgba(59,130,246,0.3)]" style={{ bottom: '15%', left: '45%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '4s' }} />

          {/* Scattered nodes with inner glow */}
          <div className="absolute w-10 h-10 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '3%', left: '45%', animation: 'pulse 6s ease-in-out infinite, glowPulse 4s ease-in-out infinite', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-8 h-8 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '10%', right: '8%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '0.4s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-12 h-12 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '8%', left: '65%', animation: 'pulse 6s ease-in-out infinite, glowPulseStrong 4s ease-in-out infinite', animationDelay: '0.8s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-9 h-9 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '20%', left: '8%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '1.2s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-11 h-11 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '25%', right: '55%', animation: 'pulse 6s ease-in-out infinite, glowPulse 4s ease-in-out infinite', animationDelay: '2s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-9 h-9 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '30%', left: '35%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '2.4s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-8 h-8 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '35%', right: '30%', animation: 'pulse 6s ease-in-out infinite, glowPulseStrong 4s ease-in-out infinite', animationDelay: '2.8s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-10 h-10 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '42%', left: '5%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '3.2s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-7 h-7 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '48%', right: '45%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '3.6s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-11 h-11 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '52%', left: '70%', animation: 'pulse 6s ease-in-out infinite, glowPulse 4s ease-in-out infinite', animationDelay: '4s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-9 h-9 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '58%', right: '5%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '4.4s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-8 h-8 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '62%', left: '30%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '4.8s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-10 h-10 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '68%', right: '20%', animation: 'pulse 6s ease-in-out infinite, glowPulseStrong 4s ease-in-out infinite', animationDelay: '5.2s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-7 h-7 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '72%', left: '55%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '5.6s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-8 h-8 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '12%', left: '85%', animation: 'pulse 6s ease-in-out infinite, glowPulse 4s ease-in-out infinite', animationDelay: '6.4s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-10 h-10 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '45%', right: '65%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '6.8s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-11 h-11 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '78%', left: '8%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '7.2s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-9 h-9 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '82%', right: '40%', animation: 'pulse 6s ease-in-out infinite, glowPulseStrong 4s ease-in-out infinite', animationDelay: '8s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-10 h-10 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '88%', left: '60%', animation: 'pulse 6s ease-in-out infinite', animationDelay: '8.8s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>
          <div className="absolute w-8 h-8 rounded-full bg-slate-700/10 border border-slate-600/25" style={{ top: '2%', right: '15%', animation: 'pulse 6s ease-in-out infinite, glowPulse 4s ease-in-out infinite', animationDelay: '9.6s', boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)' }}><div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} /></div>

          {/* Floating particles */}
          {[
            { top: 3, left: 12 }, { top: 8, left: 67 }, { top: 15, left: 34 }, { top: 6, left: 89 },
            { top: 22, left: 5 }, { top: 19, left: 52 }, { top: 28, left: 78 }, { top: 12, left: 23 },
            { top: 35, left: 41 }, { top: 31, left: 93 }, { top: 42, left: 17 }, { top: 38, left: 62 },
            { top: 48, left: 8 }, { top: 45, left: 85 }, { top: 55, left: 29 }, { top: 52, left: 71 },
            { top: 61, left: 47 }, { top: 58, left: 3 }, { top: 67, left: 56 }, { top: 64, left: 91 },
            { top: 73, left: 19 }, { top: 70, left: 38 }, { top: 79, left: 82 }, { top: 76, left: 64 },
            { top: 85, left: 11 }, { top: 82, left: 45 }, { top: 91, left: 73 }, { top: 88, left: 27 },
            { top: 94, left: 58 }, { top: 97, left: 6 }, { top: 4, left: 48 }, { top: 17, left: 76 },
            { top: 26, left: 31 }, { top: 33, left: 14 }, { top: 44, left: 69 }, { top: 54, left: 95 },
            { top: 63, left: 22 }, { top: 72, left: 51 }, { top: 81, left: 36 }, { top: 89, left: 84 }
          ].map((pos, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-blue-400 rounded-full opacity-50"
              style={{
                top: `${pos.top}%`,
                left: `${pos.left}%`,
                animation: `float ${5 + (i % 5)}s ease-in-out infinite`,
                animationDelay: `${(i % 30) / 10}s`
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-10px); }
          75% { transform: translateY(-30px) translateX(5px); }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 15px rgba(100, 116, 139, 0.2); }
          50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.6), 0 0 50px rgba(59, 130, 246, 0.3); }
        }
        @keyframes glowPulseStrong {
          0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.8), 0 0 60px rgba(59, 130, 246, 0.4); }
        }
      `}</style>
      
      {/* Content */}
      <div className="relative z-10 min-h-full flex flex-col">
        {/* Hero Chat Section */}
        <div className="flex-shrink-0 px-6 pt-20 pb-8 flex flex-col items-center">
        {/* User Space Badge */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 backdrop-blur-lg border border-blue-500/20 rounded-full px-5 py-2 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-gray-300">Manan</span>
            <span className="text-sm text-gray-500">›</span>
            <span className="text-sm text-blue-400">Manufacturing</span>
          </div>
        </div>

        {/* Main Heading */}
        <h1 className="text-4xl font-bold text-white mb-10 text-center font-heading">
          Start vibing with your data
        </h1>

        {/* Chat Input Container */}
        <div className="w-full max-w-3xl">
          <form onSubmit={handleChatSubmit}>
            <div className="rounded-xl border border-blue-500/10 shadow-[0_0_20px_rgba(59,130,246,0.15)] hover:shadow-[0_0_25px_rgba(59,130,246,0.25)] transition-all duration-200" style={{ backgroundColor: 'rgba(12, 18, 34, 0.85)' }}>
              {/* Input Area */}
              <div className="p-4 pb-2 relative">
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onFocus={() => setIsTyping(false)}
                  onBlur={() => !chatInput && setIsTyping(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleChatSubmit(e);
                    }
                  }}
                  placeholder={displayedPlaceholder || "Ask anything..."}
                  rows={2}
                  className="w-full bg-transparent text-white text-base resize-none focus:outline-none placeholder:text-gray-500 leading-relaxed"
                  style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                />
                {/* Minimal footer line - data sources indicator */}
                {chatInput.trim() && currentConnectors.length > 0 && (
                  <p className="absolute bottom-5 left-4 text-[10px] text-gray-600/70 tracking-wide pointer-events-none">
                    ... querying {currentConnectors.length} connected data source{currentConnectors.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
              
              {/* Bottom Controls Bar */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  {/* Add Data Source Button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/app/connections');
                    }}
                    className="w-8 h-8 rounded-full bg-slate-800/60 hover:bg-slate-700 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                    title="Add data source"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  
                  {/* Agent Selector */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAgentDropdown(!showAgentDropdown);
                        setShowModelDropdown(false);
                      }}
                      className="flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-slate-800/60 hover:bg-slate-700 transition-colors text-xs text-gray-300 hover:text-white"
                    >
                      <span className="text-[11px]">{selectedAgent.icon}</span>
                      <span>{selectedAgent.name}</span>
                      <ChevronUp className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${showAgentDropdown ? '' : 'rotate-180'}`} />
                    </button>
                    
                    {/* Agent Dropdown */}
                    {showAgentDropdown && (
                      <div className="absolute bottom-full left-0 mb-2 w-56 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl z-[100] overflow-hidden">
                        <div className="p-1.5">
                          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2.5 py-1.5">Data Apps</p>
                          {agents.dataApps.map((agent) => (
                            <button
                              key={agent.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAgent(agent);
                                setShowAgentDropdown(false);
                              }}
                              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-800 transition-colors ${
                                selectedAgent.id === agent.id ? 'bg-slate-800' : ''
                              }`}
                            >
                              <span className="text-sm">{agent.icon}</span>
                              <span className="text-xs font-medium text-white">{agent.name}</span>
                            </button>
                          ))}
                        </div>
                        
                        <div className="border-t border-slate-700/50 mx-1.5"></div>
                        
                        <div className="p-1.5">
                          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2.5 py-1.5">Data Discovery</p>
                          {agents.dataDiscovery.map((agent) => (
                            <button
                              key={agent.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAgent(agent);
                                setShowAgentDropdown(false);
                              }}
                              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-800 transition-colors ${
                                selectedAgent.id === agent.id ? 'bg-slate-800' : ''
                              }`}
                            >
                              <span className="text-sm">{agent.icon}</span>
                              <span className="text-xs font-medium text-white">{agent.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Model Selector */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowModelDropdown(!showModelDropdown);
                        setShowAgentDropdown(false);
                      }}
                      className="flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-slate-800/60 hover:bg-slate-700 transition-colors text-xs text-gray-300 hover:text-white"
                    >
                      <span>{selectedModel.name}</span>
                      <ChevronUp className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${showModelDropdown ? '' : 'rotate-180'}`} />
                    </button>
                    
                    {/* Model Dropdown */}
                    {showModelDropdown && (
                      <div className="absolute bottom-full left-0 mb-2 w-48 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl z-[100] overflow-hidden">
                        <div className="p-1.5">
                          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2.5 py-1.5">Select Model</p>
                          {models.map((model) => (
                            <button
                              key={model.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedModel(model);
                                setShowModelDropdown(false);
                              }}
                              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-800 transition-colors ${
                                selectedModel.id === model.id ? 'bg-slate-800' : ''
                              }`}
                            >
                              <div className="text-left">
                                <p className="text-xs font-medium text-white">{model.name}</p>
                                <p className="text-[10px] text-gray-500">{model.provider}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Voice Button */}
                  <button
                    type="button"
                    className="w-8 h-8 rounded-full bg-slate-800/60 hover:bg-slate-700 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                    title="Voice input"
                  >
                    <Mic className="w-4 h-4" />
                  </button>
                  
                  {/* Send Button */}
                  <button
                    type="submit"
                    disabled={!chatInput.trim()}
                    className="w-8 h-8 rounded-full bg-slate-800/60 hover:bg-slate-700 disabled:bg-slate-800/60 disabled:cursor-not-allowed flex items-center justify-center text-gray-300 hover:text-white disabled:text-gray-500 transition-all duration-200"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Data Connectors */}
              <div className="bg-slate-950/60 px-4 py-3 rounded-b-xl">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500 font-medium">Data Sources:</span>
                  {/* Connected Data Sources */}
                  {currentConnectors.length > 0 ? (
                    <>
                      {currentConnectors.map((connector) => (
                        <button
                          key={connector.id}
                          type="button"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 bg-slate-800/70 text-gray-300 hover:text-white hover:bg-slate-700/80"
                          title={`${connector.tables} tables`}
                        >
                          <img 
                            src={connectorLogos[connector.type]} 
                            alt={connector.name}
                            className="w-3.5 h-3.5"
                          />
                          <span>{connector.name}</span>
                          <span className="text-[10px] text-gray-500">({connector.tables})</span>
                        </button>
                      ))}
                    </>
                  ) : (
                    <span className="text-xs text-gray-500 italic">No connections in this workspace</span>
                  )}}
                  
                  {/* Add Connection Button */}
                  <div className="relative group">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/app/connections');
                      }}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-blue-400 hover:text-white bg-blue-500/15 hover:bg-blue-500/30 transition-all duration-200"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    {/* Tooltip for + button */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-slate-900 border border-slate-700/50 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="p-3">
                        <p className="text-sm font-medium text-white mb-1">Add Data Connection</p>
                        <p className="text-xs text-gray-400">Connect a new data source to this workspace</p>
                      </div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px]">
                        <div className="border-8 border-transparent border-t-slate-700/50"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Tasks/Apps Section */}
      <div className="flex flex-col px-6 pb-6 mt-8">
        {/* Tabs - Outside container */}
        <div className="flex items-center mb-4 max-w-6xl mx-auto w-full">
          <div className="flex items-center">
            <button
              onClick={() => setActiveTab('apps')}
              className={`flex items-center gap-2 text-base font-medium transition-colors ${
                activeTab === 'apps'
                  ? 'text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <Globe className="w-4 h-4" />
              Deployed Apps
            </button>
            <span className="text-gray-600 mx-3">|</span>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`flex items-center gap-2 text-base font-medium transition-colors ${
                activeTab === 'tasks'
                  ? 'text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <FileText className="w-4 h-4" />
              Recent Sessions
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div className="rounded-xl border border-blue-500/10 flex flex-col overflow-hidden max-w-6xl mx-auto w-full min-h-[500px]" style={{ backgroundColor: 'rgba(12, 18, 34, 0.85)' }}>
          {/* Table */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'tasks' ? (
              <table className="w-full">
                <thead className="bg-slate-950/60">
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-slate-700/30">
                    <th className="px-6 py-4 w-36 font-medium">ID</th>
                    <th className="px-6 py-4 w-[420px] font-medium">Session</th>
                    <th className="px-6 py-4 w-40 font-medium">Last Modified</th>
                    <th className="px-6 py-4 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {recentTasks.map((task, index) => (
                    <tr 
                      key={task.id} 
                      className={`group hover:bg-slate-800/50 cursor-pointer transition-colors ${
                        index !== recentTasks.length - 1 ? 'border-b border-slate-800/20' : ''
                      }`}
                    >
                      <td className="px-6 py-6 text-sm text-gray-500 font-mono align-top">{task.id}</td>
                      <td className="px-6 py-6 align-top">
                        <div className="space-y-1">
                          <p className="text-sm text-white font-medium">{task.title}</p>
                          {task.fork && (
                            <p className="text-sm">
                              <span className="text-teal-400">Fork</span>
                              <span className="inline-flex items-center justify-center w-5 h-5 mx-1 text-xs bg-teal-500/20 text-teal-400 rounded">{task.fork}</span>
                              <span className="text-teal-400">from</span>
                              <span className="text-gray-500"> - &lt;analysis&gt;**original_problem_statement:** {task.task}</span>
                            </p>
                          )}
                          {!task.fork && (
                            <p className="text-sm text-gray-500">{task.task}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-6 text-sm text-gray-500 align-top">{task.lastModified}</td>
                      <td className="px-6 py-6 align-top">
                        <button className="p-1.5 text-gray-600 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all">
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deployedApps.map((app) => (
                  <div 
                    key={app.id}
                    className="bg-slate-800/40 rounded-xl p-4 hover:bg-slate-800/60 transition-all cursor-pointer group"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <h3 className="text-white font-medium text-base">{app.name}</h3>
                      <button className="p-1.5 text-gray-600 hover:text-white hover:bg-slate-700/50 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Type */}
                    <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-medium mb-4">{app.type}</span>

                    {/* Status */}
                    <div className="flex items-center gap-1.5 mb-3">
                      <span className={`w-2 h-2 rounded-full ${
                        app.status === 'completed' ? 'bg-green-400' : 
                        app.status === 'running' ? 'bg-amber-400 animate-pulse' : 
                        'bg-red-400'
                      }`}></span>
                      <span className={`text-xs font-medium capitalize ${
                        app.status === 'completed' ? 'text-green-400' : 
                        app.status === 'running' ? 'text-amber-400' : 
                        'text-red-400'
                      }`}>{app.status}</span>
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-slate-800/40 rounded-lg px-2.5 py-2">
                        <p className="text-xs text-gray-500 mb-0.5">Rows</p>
                        <p className="text-sm text-white font-medium">{formatNumber(app.rows)}</p>
                      </div>
                      <div className="bg-slate-800/40 rounded-lg px-2.5 py-2">
                        <p className="text-xs text-gray-500 mb-0.5">Columns</p>
                        <p className="text-sm text-white font-medium">{app.columns}</p>
                      </div>
                      <div className="bg-slate-800/40 rounded-lg px-2.5 py-2">
                        <p className="text-xs text-gray-500 mb-0.5">Exec Time</p>
                        <p className="text-sm text-white font-medium">{app.executionTime}s</p>
                      </div>
                      <div className="bg-slate-800/40 rounded-lg px-2.5 py-2">
                        <p className="text-xs text-gray-500 mb-0.5">DQ Score</p>
                        <p className={`text-sm font-medium ${app.dqScore >= 90 ? 'text-green-400' : app.dqScore >= 80 ? 'text-yellow-400' : 'text-red-400'}`}>{app.dqScore}%</p>
                      </div>
                    </div>

                    {/* Data Sources */}
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {app.dataSources.map((source, idx) => (
                        <span key={idx} className="text-xs bg-slate-800/60 text-gray-400 px-2 py-1 rounded-md">
                          {getSourceIcon(source)} {source}
                        </span>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-700/30">
                      <div className="text-xs text-gray-500">
                        <span className="text-gray-400">{app.createdBy}</span> • {app.createdDate}
                      </div>
                      <div className="relative group/share">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer hover:text-gray-300 transition-colors">
                          <Users className="w-3.5 h-3.5" />
                          <span>{app.sharedWith.length}</span>
                        </div>
                        {/* Hover Tooltip */}
                        <div className="absolute bottom-full right-0 mb-2 w-48 bg-slate-900 border border-slate-700/50 rounded-lg shadow-xl opacity-0 invisible group-hover/share:opacity-100 group-hover/share:visible transition-all duration-200 z-50">
                          <div className="px-3 py-2 border-b border-slate-700/50">
                            <p className="text-xs font-medium text-gray-300">Shared with</p>
                          </div>
                          <div className="max-h-40 overflow-y-auto py-1">
                            {app.sharedWith.map((team, idx) => (
                              <div key={idx} className="px-3 py-1.5 text-xs text-gray-400 hover:bg-slate-800/50">
                                {team}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700/30 bg-slate-950/60">
            <span className="text-sm text-gray-500">
              Showing 1-{activeTab === 'apps' ? '6' : '6'} out of {activeTab === 'apps' ? '6' : '63'}
            </span>
            
            <div className="flex items-center gap-2">
              <button className="p-1 text-gray-500 hover:text-white transition-colors">
                <ChevronUp className="w-4 h-4 -rotate-90" />
              </button>
              <button className="w-8 h-8 flex items-center justify-center text-sm bg-slate-800 text-white rounded">1</button>
              <button className="w-8 h-8 flex items-center justify-center text-sm text-gray-500 hover:text-white transition-colors">2</button>
              <button className="p-1 text-gray-500 hover:text-white transition-colors">
                <ChevronUp className="w-4 h-4 rotate-90" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{activeTab === 'apps' ? 'Apps' : 'Sessions'} per page:</span>
              <select className="bg-slate-800 text-white text-sm px-2 py-1 rounded border border-slate-700 focus:outline-none">
                <option>50</option>
                <option>25</option>
                <option>10</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default HomePage;
